import { Component, HostListener } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { DriverRecordRequestService } from '../../../services/driverrecordrequest.service';
import { GeneralService } from '../../../services/general.service';
import { IWizard } from '../../../interfaces/iwizard';
import { WizardSteps } from '../../../interfaces/wizard-steps';
import { Router, ActivatedRoute, ParamMap } from '@angular/router';
import { AuthenticationService } from '../../../services/authentication.service';
import { UserProfile } from '../../../interfaces/UserProfile';
import RenderDriverReportRequest from '../../../interfaces/renderReportRequest';
import { DriverReportView } from '../../../interfaces/driverReportView';
import { DriverService } from '../../../services/driver.service';
import { DateTime } from 'luxon';
import { TranslatorService } from '../../../services/translator.service';

@Component({
    selector: 'app-order-success',
    templateUrl: './order-success.component.html',
    styleUrls: ['./order-success.component.scss'],
    standalone: false
})
export class OrderSuccessComponent {
     public wizard!: IWizard;

     ccpToken: string = '';
     isLoading: boolean = true;
     userProfile?: UserProfile;
     username?: string;
     authMethod: string = 'Anonymous';
     accessedVia: string = 'DriverWizard';
     accessedBy: string = '';
     routeParams: ParamMap = {} as ParamMap;

     constructor(public _router: Router,
          public service: DriverRecordRequestService,
          public generalService: GeneralService,
          public authenticationService: AuthenticationService,
          public driverService: DriverService,
          private route: ActivatedRoute,
          private translatorService: TranslatorService
     ) { 
          this.wizard = this.service.getWizard();
     }

     ngOnInit(): void {
          window.history.pushState(null, "", window.location.href);
          this.isLoading = true;
          setTimeout(() => this.scrollToTop(), 500);
          this.translatorService.restoreBackupLanguage();
          this.routeParams = this.route.snapshot.queryParamMap;
          this.username = this.authenticationService.getUserName();
          this.userProfile = this.authenticationService.getUserProfile();


          let reload = false;
          if (this.routeParams && this.routeParams.has('token') && this.routeParams.get('token')?.toString()) {
               this.ccpToken = this.routeParams.get('token')?.toString() ?? '';
          }
          const navigationEntries = window.performance.getEntriesByType("navigation");
          if (window.performance && window.performance.navigation.type === 1) {
               // if reload we will not just reload the stored auth.
               reload = true;
          } else {
               this.driverService.clearAuthCode();
          }

          if (this.ccpToken) {
               this.accessedBy = this.service.driverRequest.emailAddress ?? '';
               if (this.username) {
                    this.authMethod = 'Login.gov';
                    this.accessedVia = 'DriverDashboard';
                    this.accessedBy = this.username;
               }

               if (!this.service.testCCPHistory(this.ccpToken) && reload === false) {
                    this.driverService.getAuthCode(this.ccpToken)
                         .then((authCode: string | null) => {
                              if (authCode) {
                                   this.generalService.pageLogging("order-success", "", "", "", "ccpToken:" + this.ccpToken);
                                   this.isLoading = false;
                              } else {
                                   this.generalService.pageLogging("order-success", "", "", "No Auth Code", "ccpToken:" + this.ccpToken);
                                   this._router.navigateByUrl('/customerSupportInfo');
                              }
                         }).catch((error: any) => {
                              this.generalService.pageLogging("order-success", "", "", error, "ccpToken:" + this.ccpToken);
                              this._router.navigateByUrl('/customerSupportInfo');
                         });
               } else if (this.driverService.getStoredAuthCode() !== '') {
                    this.driverService.state.authCode = this.driverService.getStoredAuthCode();
                    this.isLoading = false;
               } else {
                    this.finish();
               }
          } else {
               this.generalService.pageLogging("order-success", "", "", "Token from URL missing!!!", "");
               this._router.navigateByUrl('/customerSupportInfo');
          }
     }

     showInBrowser() {
          if (this.driverService.state.authCode && this.driverService.state.authCode.length > 0) {
               const request: RenderDriverReportRequest = { asPdf: false, authCode: this.driverService.state.authCode, driverIP: '' };
               this.service.getEndUserIp()
                    .then((result: any) => {
                         request.driverIP = result;
                         const reportView: DriverReportView = {
                              accessedVia: this.accessedVia,
                              accessedBy: this.accessedBy,
                              accessorCustomerId: '2147483647',
                              authCode: this.driverService.state.authCode,
                              authenticationMethod: this.authMethod,
                              accessedDateTime: DateTime.now().toJSDate()
                         }
                         this.service.openDriverReport(request, reportView)
                              .then((v: any) => {
                                   this.openInBrowser(v);
                              })
                              .catch((error: any) => {
                                   this.generalService.pageLogging("order-success", "", "", error, "openDriverReport");
                              });
                    }).catch((error: any) => {
                         console.error(error);
                         const reportView: DriverReportView = {
                              accessedVia: this.accessedVia,
                              accessedBy: this.accessedBy,
                              accessorCustomerId: '2147483647',
                              authCode: this.driverService.state.authCode,
                              authenticationMethod: this.authMethod,
                              accessedDateTime: DateTime.now().toJSDate()
                         }
                         this.service.openDriverReport(request, reportView)
                              .then((v: any) => {
                                   this.openInBrowser(v);
                              })
                              .catch((error: any) => {
                                   this.generalService.pageLogging("order-success", "", "", error, "getEndUserIp");
                              });
                    });
          } else {
               this._router.navigateByUrl('/');
          }
     }

     openInBrowser(contentToRender: string) {
          if (contentToRender && contentToRender.length === 0) {
               this._router.navigateByUrl('/customerSupportInfo');
          }
          const newWindow = window.open();
          if (newWindow) {
               newWindow.document.title = this.generalService.getTranslation('DriverRecordTitle', 'customer')
               newWindow.document.write(contentToRender);
               newWindow.document.close();
               newWindow.focus()
          }
     }

     downloadPdf() {
          const reportView: DriverReportView = {
               accessedVia: this.accessedVia,
               accessedBy: this.accessedBy,
               accessorCustomerId: '2147483647',
               authCode: this.driverService.state.authCode,
               authenticationMethod: this.authMethod,
               accessedDateTime: new Date()
          }
          this.service.getDriverReportPdf(this.driverService.state.authCode, reportView)
               .then((v: any) => this.openPdf(v))
               .catch((error: any) => {
                    this.generalService.pageLogging("order-success", "", "", error, "getDriverReportPdf");
               });
     }

     openPdf(buffer: any) {
          const filename = "My_PSP_Driver_Report.pdf";
          const element = document.createElement('a');
          element.setAttribute('href', 'data:application/octet-stream;base64,' + buffer);
          element.setAttribute('download', filename);
          element.style.display = 'none';
          document.body.appendChild(element);
          element.click();
          document.body.removeChild(element);
     }

     translate(translationTag: string): string {
          let translatedText: string = this.generalService.getTranslation(translationTag, 'customer')
          if (translatedText === undefined) {
               translatedText = this.generalService.getTranslation(translationTag, 'customer');
          }
          return translatedText;
     }

     get hasErrors(): boolean {
          return true;
     }

     finish() {
          if (this.username) {
               this.scrollToTop();
               this._router.navigateByUrl('/driverDashboard');
          } else {
               this.scrollToTop();
               this.driverService.clearAuthCode();
               this._router.navigateByUrl('/home');
          }
     }

     scrollToTop() {
          window.scrollTo(0, 0);
     }

     navigateToStateMVR() {
          this._router.navigate(['/MvrByState']);
     }
}
